import { useMemo } from "react";
import { clsx } from "clsx";
import { useExpenses } from "../../../lib/expenses/useExpenses";
import { useInvoices } from "../../../lib/invoices/useInvoices";
import { useReceipts } from "../../../lib/receipts/useReceipts";
import type { OrgRole } from "../../../lib/org/types";
import {
  Card,
  formatCents,
  withinRange,
  computeRange,
  type RangePreset,
} from "./shared";

// KpiRow — three quick-read stats above the chart grid.
//   • Burn rate: avg monthly expense over the last 3 full months
//     (today-90d, today), divided by 3.
//   • Net margin: (revenue − expenses) / revenue × 100 across the range.
//     Revenue comes from incoming invoices — matches CashFlow.tsx's mapping
//     (incoming = money IN). When revenue is 0 we render "—" (not Infinity).
//   • Receipt compliance: % of expenses in the window with a linked receipt.
//
// Role gating per Reports.tsx precedent: employee sees burn only (no org-wide
// revenue or compliance totals), admin/owner/accountant see all three,
// auditor read-only sees all three.

const MS_PER_DAY = 86_400_000;

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export interface KpiRowProps {
  preset: RangePreset;
  role: OrgRole | null;
  submittedBy?: string | null;
}

export function KpiRow({ preset, role, submittedBy }: KpiRowProps) {
  const { expenses } = useExpenses();
  const { invoices } = useInvoices();
  const { receipts } = useReceipts();

  const showMargin = role !== "employee";
  const showCompliance = role !== "employee";

  const burn = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startBound = new Date(today.getTime() - 90 * MS_PER_DAY);
    const startIso = toIsoDate(startBound);
    const endIso = toIsoDate(today);
    let sum = 0;
    let seen = 0;
    for (const e of expenses) {
      if (submittedBy && e.submittedBy !== submittedBy) continue;
      if (!e.occurredOn) continue;
      const day = e.occurredOn.slice(0, 10);
      if (day >= startIso && day < endIso) {
        sum += e.amountCents;
        seen += 1;
      }
    }
    return { avgCents: Math.round(sum / 3), sampled: seen };
  }, [expenses, submittedBy]);

  const { marginPct, marginKnown } = useMemo(() => {
    if (!showMargin) return { marginPct: 0, marginKnown: false };
    const { startIso, endIso } = computeRange(preset);
    let expTotal = 0;
    for (const e of expenses) {
      if (!withinRange(e.occurredOn, startIso, endIso)) continue;
      expTotal += e.amountCents;
    }
    let revenue = 0;
    for (const inv of invoices) {
      if (!withinRange(inv.issuedOn, startIso, endIso)) continue;
      // Matches CashFlow.tsx: incoming = money IN = revenue.
      if (inv.direction === "incoming") revenue += inv.amountCents;
    }
    if (revenue <= 0) return { marginPct: 0, marginKnown: false };
    return {
      marginPct: ((revenue - expTotal) / revenue) * 100,
      marginKnown: true,
    };
  }, [expenses, invoices, preset, showMargin]);

  const compliance = useMemo(() => {
    if (!showCompliance) return { attached: 0, total: 0 };
    const { startIso, endIso } = computeRange(preset);
    const linked = new Set<string>();
    for (const r of receipts) {
      if (r.linkedExpenseId) linked.add(r.linkedExpenseId);
    }
    let attached = 0;
    let total = 0;
    for (const e of expenses) {
      if (!withinRange(e.occurredOn, startIso, endIso)) continue;
      total += 1;
      if (linked.has(e.id)) attached += 1;
    }
    return { attached, total };
  }, [expenses, receipts, preset, showCompliance]);

  return (
    <div
      data-testid="report-kpi-row"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
    >
      <Card testId="kpi-burn">
        <KpiLabel>Burn rate</KpiLabel>
        {burn.sampled === 0 ? (
          <KpiEmpty>Need 3 months of data</KpiEmpty>
        ) : (
          <>
            <div className="text-2xl font-semibold text-slate-900 mt-0.5 tabular-nums">
              {formatCents(burn.avgCents)}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Avg monthly spend · last 3 months
            </div>
          </>
        )}
      </Card>

      {showMargin && (
        <Card testId="kpi-margin">
          <KpiLabel>Net margin</KpiLabel>
          {!marginKnown ? (
            <>
              <div className="text-2xl font-semibold text-slate-400 mt-0.5">
                —
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                No revenue recorded in window
              </div>
            </>
          ) : (
            <>
              <div
                className={clsx(
                  "text-2xl font-semibold mt-0.5 tabular-nums",
                  marginPct >= 0 ? "text-emerald-600" : "text-rose-600",
                )}
              >
                {marginPct >= 0 ? "+" : ""}
                {marginPct.toFixed(1)}%
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                (Revenue − Expenses) ÷ Revenue
              </div>
            </>
          )}
        </Card>
      )}

      {showCompliance && (
        <Card testId="kpi-receipts">
          <KpiLabel>Receipt compliance</KpiLabel>
          {compliance.total === 0 ? (
            <KpiEmpty>No expenses in window</KpiEmpty>
          ) : (
            <>
              <div className="text-2xl font-semibold text-slate-900 mt-0.5 tabular-nums">
                {compliance.attached}/{compliance.total}
                <span className="text-slate-500 font-normal text-base ml-2">
                  ({Math.round((compliance.attached / compliance.total) * 100)}
                  %)
                </span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Expenses with a linked receipt
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}

function KpiLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
      {children}
    </div>
  );
}

function KpiEmpty({ children }: { children: React.ReactNode }) {
  return (
    <>
      <div className="text-2xl font-semibold text-slate-400 mt-0.5">—</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{children}</div>
    </>
  );
}
